/** What each line of a unified diff IS, decided in one forward pass — the
 * SEMANTIC half of patch rendering, shared by every skin.
 *
 * This lives in `thread/` beside the slot contract, not in a skin, because
 * classification is a fact about the diff and not about its presentation:
 * three skins each writing their own pass is how two of them shipped the rule
 * everyone writes first — tint by the first character, rule out `+++`/`---`
 * by prefix — which is wrong in both directions. `--- a/x` inside a hunk is a
 * removed line whose content begins `-- `, and `-1 file changed` in a summary
 * tail is not a removal at all. What decides is WHERE the line is, so the
 * pass carries one bit of state — whether a hunk is open — and everything
 * outside a hunk is chrome by construction, no prefix table required.
 *
 * Presentation stays with each skin: what a `del` looks like — a red tint on
 * the raw line, a stripped body with the sign redrawn in a gutter, a count in
 * a footer — is the skin's answer, which is why a line carries BOTH texts.
 *
 * `\ No newline at end of file` is chrome that does NOT close the hunk — it
 * appears BETWEEN a removal and its replacement, and treating it as a
 * terminator makes every `+` after it read as a filename.
 *
 * Classify ONCE, from the whole text, and only then cap or slice: a line's
 * kind is not a property of the line alone, so a pass over a capped slice
 * reads the tail's context as file chrome. */
export namespace TurnDiff {
  export type Kind = "add" | "del" | "ctx" | "hunk" | "meta";

  /** One classified line. `raw` is the line verbatim; `text` has the sign
   * column stripped for `add`/`del`/`ctx` (context loses its leading space to
   * the same column, so all three kinds sit on one left edge) and is verbatim
   * for `hunk` and `meta`, which have no sign to strip. */
  export type Line = { kind: Kind; raw: string; text: string };

  export function classify(lines: string[]): Line[] {
    const rows: Line[] = [];
    let inHunk = false;
    for (const line of lines) {
      if (line.startsWith("@@")) {
        inHunk = true;
        rows.push({ kind: "hunk", raw: line, text: line });
        continue;
      }
      if (!inHunk || line.startsWith("\\")) {
        rows.push({ kind: "meta", raw: line, text: line });
        continue;
      }
      if (line.startsWith("+")) {
        rows.push({ kind: "add", raw: line, text: line.slice(1) });
        continue;
      }
      if (line.startsWith("-")) {
        rows.push({ kind: "del", raw: line, text: line.slice(1) });
        continue;
      }
      if (line.startsWith(" ")) {
        rows.push({ kind: "ctx", raw: line, text: line.slice(1) });
        continue;
      }
      // A blank line is context a producer wrote without its leading space —
      // every dialect on this host emits at least some of them.
      if (line === "") {
        rows.push({ kind: "ctx", raw: "", text: "" });
        continue;
      }
      // Anything else cannot be hunk content: the next file's header, a
      // `N files changed` tail, a binary notice. The hunk is over.
      inHunk = false;
      rows.push({ kind: "meta", raw: line, text: line });
    }
    return rows;
  }
}
